import { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { get, post, put, del } from './api';
import { ContentEditor } from './components/ContentEditor';
import { AnnouncementsView, OnboardingView, AcknowledgmentsView, IncidentsView, ExpiryBanner } from './components/V4Features';
import { ComplianceTracker } from './components/ComplianceTracker';
import { EquipmentTracker, ChecklistsView, DocumentVault, FAQView, ManagerSOPsView, ApplicationFormAdmin, ManagerAssistant } from './components/Operations';
import { OffersView } from './components/OfferEmails';
import OfferSign from './components/OfferSign';
import { LeaderboardView, QuickReferenceView, BrandingSettings, StaffProfilesView, FirstDayWelcome } from './components/V5Features';
import { QuickRefEditor } from './components/QuickRefEditor';
import { MenuCatalog } from './components/MenuCatalog';
import { TempLogger } from './components/TempLogger';
import { CareersAdminView } from './components/OperationsHub';
import { CandidatesView } from './components/Candidates';
import { ScheduleView } from './components/Schedule';
import { ScanInboxView } from './components/ScanInbox';
import './App.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const catColor = { Foundation: '#c9a96e', Operations: '#f59e0b', Activity: '#e63c3c', Safety: '#10b981', Systems: '#6366f1', Bar: '#a855f7', Kitchen: '#f97316', Events: '#06b6d4' };
const roleColor = { owner: '#c9a96e', admin: '#6366f1', lead: '#f59e0b', staff: '#10b981' };
const roleLabel = { owner: 'Owner', admin: 'Manager', lead: 'Lead', staff: 'Staff' };

function Badge({ label, color }) {
  return <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>;
}

function ProgressBar({ pct, color = '#c9a96e', height = 6 }) {
  return <div style={{ background: '#2a1f0f', borderRadius: 4, height, overflow: 'hidden' }}><div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s ease' }} /></div>;
}

function Modal({ children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 14, padding: 32, maxWidth: wide ? 860 : 660, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.7)' }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const styles = {
    primary: { background: '#c9a96e', color: '#1a1208', border: 'none' },
    secondary: { background: 'transparent', color: '#8a7055', border: '1px solid #3a2a12' },
    danger: { background: '#e63c3c22', color: '#e63c3c', border: '1px solid #e63c3c44' },
    success: { background: '#10b98122', color: '#10b981', border: '1px solid #10b98144' },
    ghost: { background: '#c9a96e18', color: '#c9a96e', border: '1px solid #c9a96e44' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...styles[variant], borderRadius: 8, padding: small ? '6px 14px' : '10px 22px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', transition: 'all 0.15s', ...style }}>
      {children}
    </button>
  );
}

// Views gated to managers/GM — used to sanitize a persisted view on refresh so a
// non-manager never restores into a blank (unauthorized) screen.
const MANAGER_VIEWS = new Set(['staff', 'sops', 'applicationform', 'assistant', 'compliance', 'documents', 'profiles', 'content', 'quickrefedit', 'users', 'changes', 'careers', 'hiring', 'offers', 'schedule', 'scan', 'settings']);
const VIEW_STORAGE_KEY = 'tnt_view';

function Input({ label, value, onChange, placeholder, type = 'text', rows, onKeyDown }) {
  const s = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>}
      {rows ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} onKeyDown={onKeyDown} style={{ ...s, resize: 'vertical' }} /> : <input type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown} style={s} />}
    </div>
  );
}

function Avatar({ name, size = 36 }) {
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  return <div style={{ width: size, height: size, borderRadius: '50%', background: '#c9a96e', color: '#1a1208', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.35, flexShrink: 0, fontFamily: "'Playfair Display', serif" }}>{initials}</div>;
}

function NavRow({ item, view, onPick, indent }) {
  const active = view === item.key;
  const ready = item.ready !== false;
  return (
    <div onClick={() => { if (!ready) return; onPick(item.key); }} style={{ padding: indent ? '8px 12px 8px 32px' : '10px 12px', borderRadius: 8, cursor: ready ? 'pointer' : 'default', marginBottom: 2, background: active ? '#c9a96e18' : 'transparent', color: active ? '#c9a96e' : (ready ? '#8a7055' : '#5a4030'), fontWeight: active ? 700 : 400, display: 'flex', alignItems: 'center', gap: 10, borderLeft: active ? '2px solid #c9a96e' : '2px solid transparent', opacity: ready ? 1 : 0.55, transition: 'all 0.12s' }}>
      <span style={{ fontSize: indent ? 14 : 15 }}>{item.icon}</span>
      <span style={{ fontSize: 13 }}>{item.label}</span>
      {!ready && <span style={{ marginLeft: 'auto', fontSize: 8, color: '#5a4030', textTransform: 'uppercase', letterSpacing: '0.06em', border: '1px solid #3a2a12', borderRadius: 4, padding: '1px 5px' }}>soon</span>}
    </div>
  );
}

function NavGroup({ icon, label, items, view, onPick, open, onToggle }) {
  const childActive = items.some(i => i.key === view);
  return (
    <div style={{ marginTop: 6 }}>
      <div onClick={onToggle} style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 2, background: (childActive && !open) ? '#c9a96e18' : 'transparent', color: childActive ? '#c9a96e' : '#8a7055', fontWeight: childActive ? 700 : 400, display: 'flex', alignItems: 'center', gap: 10, borderLeft: childActive ? '2px solid #c9a96e' : '2px solid transparent', transition: 'all 0.12s' }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 13 }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
      </div>
      {open && items.map(n => <NavRow key={n.key} item={n} view={view} onPick={onPick} indent={true} />)}
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
function Login() {
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => { get('/branding/public').then(b => { if (b && b.logo_url) setLogo(b.logo_url); }).catch(() => {}); }, []);

  async function handleLogin() {
    setError(''); setLoading(true);
    try { await login(form.username, form.password); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const onKeyDown = e => { if (e.key === 'Enter' && !loading && form.username && form.password) handleLogin(); };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0702', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 16, padding: '48px 40px', maxWidth: 400, width: '100%', textAlign: 'center' }}>
        {logo
          ? <img src={logo} alt="Logo" style={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain', marginBottom: 16 }} onError={e=>{e.target.style.display='none'}} />
          : <div style={{ fontSize: 52, marginBottom: 12 }}>🪓</div>}
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>Tee & Timber</h1>
        <p style={{ color: '#c9a96e', fontSize: 13, marginBottom: 32, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>Training Hub</p>
        <Input label="Username" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="your username" onKeyDown={onKeyDown} />
        <Input label="Password" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="your password" onKeyDown={onKeyDown} />
        {error && <p style={{ color: '#e63c3c', fontSize: 13, marginBottom: 14 }}>{error}</p>}
        <Btn onClick={handleLogin} disabled={loading || !form.username || !form.password} style={{ width: '100%' }}>
          {loading ? 'Signing in...' : 'Sign In →'}
        </Btn>
        <p style={{ color: '#5a4030', fontSize: 12, marginTop: 20 }}>Contact your manager if you need login help.</p>
      </div>
    </div>
  );
}

// ── Change Password ───────────────────────────────────────────────────────────
function ChangePasswordModal({ onClose, required }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ current: '', newPw: '', confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handle() {
    if (form.newPw !== form.confirm) return setError('Passwords do not match');
    if (form.newPw.length < 4) return setError('Password must be at least 4 characters');
    try {
      await post('/auth/change-password', { current_password: form.current, new_password: form.newPw });
      setSuccess(true);
      setUser(u => ({ ...u, first_login: 0 }));
      setTimeout(onClose, 1500);
    } catch (e) { setError(e.message); }
  }

  return (
    <Modal onClose={required ? null : onClose}>
      <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 8 }}>{required ? 'Set Your Password' : 'Change Password'}</h2>
      {required && <p style={{ color: '#8a7055', fontSize: 14, marginBottom: 24 }}>Welcome! Please set a personal password before continuing.</p>}
      {!required && <Input label="Current Password" type="password" value={form.current} onChange={e => setForm(p => ({ ...p, current: e.target.value }))} />}
      <Input label="New Password" type="password" value={form.newPw} onChange={e => setForm(p => ({ ...p, newPw: e.target.value }))} />
      <Input label="Confirm New Password" type="password" value={form.confirm} onChange={e => setForm(p => ({ ...p, confirm: e.target.value }))} />
      {error && <p style={{ color: '#e63c3c', fontSize: 13, marginBottom: 12 }}>{error}</p>}
      {success && <p style={{ color: '#10b981', fontSize: 13, marginBottom: 12 }}>✓ Password updated!</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={handle} disabled={!form.newPw || !form.confirm}>Save Password</Btn>
        {!required && <Btn variant="secondary" onClick={onClose}>Cancel</Btn>}
      </div>
    </Modal>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ modules, myProgress, allProgress, users, setView }) {
  const { user } = useAuth();
  const isManager = ['owner', 'admin'].includes(user.role);

  const [certAlert, setCertAlert] = useState(null);
  useEffect(() => {
    if (isManager) { get('/certs/expiry').then(setCertAlert).catch(() => {}); }
  }, [isManager]);

  const certified = modules.filter(m => myProgress.find(p => p.module_id === m.id && p.certified));
  const quizPassed = modules.filter(m => myProgress.find(p => p.module_id === m.id && p.quiz_passed));
  const pct = modules.length ? Math.round((certified.length / modules.length) * 100) : 0;

  const pendingSignoffs = isManager ? (allProgress || []).filter(p => p.quiz_passed && !p.certified && p.hands_on_status === 'pending') : [];

  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: '#f5e6c8', marginBottom: 4 }}>Welcome, {user.name} 👋</h2>
      <p style={{ color: '#8a7055', marginBottom: 32 }}>{isManager ? 'Manager Dashboard — Tee & Timber Training Hub' : 'Your training progress at Tee & Timber'}</p>

      {isManager && certAlert && (certAlert.summary.expired > 0 || certAlert.summary.soon > 0 || certAlert.summary.missing_required > 0) && (
        <div
          onClick={() => setView && setView('compliance')}
          style={{ cursor: 'pointer', background: certAlert.summary.expired > 0 || certAlert.summary.missing_required > 0 ? '#e63c3c14' : '#f59e0b14', border: `1px solid ${certAlert.summary.expired > 0 || certAlert.summary.missing_required > 0 ? '#e63c3c44' : '#f59e0b44'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
        >
          <div>
            <div style={{ color: certAlert.summary.expired > 0 || certAlert.summary.missing_required > 0 ? '#e63c3c' : '#f59e0b', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>⚠️ Certifications need attention</div>
            <div style={{ color: '#d4b896', fontSize: 13 }}>
              {[
                certAlert.summary.expired > 0 ? `${certAlert.summary.expired} expired` : null,
                certAlert.summary.soon > 0 ? `${certAlert.summary.soon} expiring within 60 days` : null,
                certAlert.summary.missing_required > 0 ? `${certAlert.summary.missing_required} missing (required)` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
          <span style={{ color: '#c9a96e', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>View details →</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Modules Available', value: modules.filter(m => m.status === 'published').length, icon: '📋', color: '#c9a96e' },
          { label: 'Quizzes Passed', value: quizPassed.length, icon: '✓', color: '#10b981' },
          { label: 'Certified Modules', value: certified.length, icon: '🏆', color: '#f59e0b' },
          ...(isManager ? [{ label: 'Pending Sign-Offs', value: pendingSignoffs.length, icon: '✍️', color: '#6366f1' }] : []),
        ].map(c => (
          <div key={c.label} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 12, padding: '20px 22px' }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>{c.icon}</div>
            <div style={{ fontSize: 30, fontFamily: "'Playfair Display', serif", color: c.color, fontWeight: 700 }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#8a7055', marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {!isManager && (
        <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 12, padding: 24, marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ color: '#f5e6c8', fontWeight: 700 }}>Overall Certification Progress</span>
            <span style={{ color: '#c9a96e', fontWeight: 700 }}>{pct}%</span>
          </div>
          <ProgressBar pct={pct} height={10} />
          <p style={{ color: '#5a4030', fontSize: 12, marginTop: 10 }}>
            {pct < 100 ? `${modules.length - certified.length} module${modules.length - certified.length !== 1 ? 's' : ''} remaining` : '✓ Fully certified!'}
          </p>
        </div>
      )}

      {isManager && pendingSignoffs.length > 0 && (
        <div style={{ background: '#6366f118', border: '1px solid #6366f144', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ color: '#a5b4fc', fontWeight: 700, marginBottom: 12 }}>⚡ Pending Hands-On Sign-Offs ({pendingSignoffs.length})</div>
          {pendingSignoffs.slice(0, 3).map(p => (
            <div key={p.id} style={{ color: '#8a7055', fontSize: 13, marginBottom: 6 }}>
              • {p.user_name} — {p.module_title} (Quiz: {p.quiz_score}%)
            </div>
          ))}
          {pendingSignoffs.length > 3 && <p style={{ color: '#5a4030', fontSize: 12, marginTop: 8 }}>+{pendingSignoffs.length - 3} more — see Staff Progress</p>}
        </div>
      )}

      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#c9a96e', marginBottom: 16 }}>
        {isManager ? 'All Training Modules' : 'Your Training Modules'}
      </h3>
      <div style={{ display: 'grid', gap: 12 }}>
        {modules.filter(m => m.status === 'published').map(m => {
          const p = myProgress.find(pr => pr.module_id === m.id);
          const cc = catColor[m.category] || '#c9a96e';
          const step = p?.certified ? 3 : p?.quiz_passed ? 2 : p?.videos_watched?.length > 0 ? 1 : 0;
          return (
            <div key={m.id} style={{ background: '#1a1208', border: `1px solid ${p?.certified ? '#10b98144' : '#3a2a12'}`, borderRadius: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: cc + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: cc, fontSize: 18, flexShrink: 0 }}>
                {m.category === 'Activity' ? '🪓' : m.category === 'Safety' ? '🛡' : m.category === 'Systems' ? '💻' : '📋'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#f5e6c8', fontWeight: 600, marginBottom: 4 }}>{m.title}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge label={m.category} color={cc} />
                  {m.pass_mark === 90 && <Badge label="90% Required" color="#e63c3c" />}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {p?.certified ? <span style={{ color: '#10b981', fontWeight: 700, fontSize: 13 }}>🏆 Certified</span>
                  : p?.quiz_passed ? <span style={{ color: '#f59e0b', fontSize: 13 }}>✓ Quiz Passed</span>
                  : p?.videos_watched?.length > 0 ? <span style={{ color: '#6366f1', fontSize: 13 }}>▶ In Progress</span>
                  : <span style={{ color: '#5a4030', fontSize: 13 }}>Not Started</span>}
                <div style={{ color: '#5a4030', fontSize: 11, marginTop: 2 }}>Step {step}/3</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Module Detail / Training ──────────────────────────────────────────────────
function ModuleView({ module, myProgress, onProgress }) {
  const [tab, setTab] = useState('overview');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const { user } = useAuth();

  const progress = myProgress.find(p => p.module_id === module.id) || {};
  const watched = progress.videos_watched || [];
  const cc = catColor[module.category] || '#c9a96e';
  const content = module.content || {};

  async function markWatched(vid) {
    await post('/progress/watch', { module_id: module.id, video_id: vid.id });
    onProgress();
  }

  async function submitQuiz() {
    setQuizLoading(true);
    const answers = module.questions.map((_, i) => quizAnswers[i] ?? -1);
    try {
      const result = await post('/progress/quiz', { module_id: module.id, answers });
      setQuizResult(result);
      onProgress();
    } catch (e) {
      try {
        const errData = JSON.parse(e.message);
        setQuizResult({ error: errData.error, ...errData });
      } catch {
        // Try to get the actual error response
        setQuizResult({ error: 'general', message: e.message });
      }
    } finally { setQuizLoading(false); }
  }

  const tabs = ['overview', 'scripts', ...(module.videos?.length ? ['videos'] : []), ...(module.questions?.length ? ['quiz'] : []), 'troubleshoot'];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Badge label={module.category} color={cc} />
        {module.pass_mark === 90 && <span style={{ marginLeft: 8 }}><Badge label="90% Pass Required" color="#e63c3c" /></span>}
        {progress.certified && <span style={{ marginLeft: 8 }}><Badge label="Certified ✓" color="#10b981" /></span>}
      </div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 6 }}>{module.title}</h2>
      <p style={{ color: '#8a7055', marginBottom: 24 }}>{module.description}</p>

      {/* 3-Step Progress */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
        {[
          { step: 1, label: 'Watch & Read', done: watched.length > 0 || !module.videos?.length },
          { step: 2, label: `Quiz (${module.pass_mark}%+)`, done: !!progress.quiz_passed },
          { step: 3, label: 'Manager Sign-Off', done: !!progress.certified },
        ].map(s => (
          <div key={s.step} style={{ background: s.done ? '#10b98118' : '#1a1208', border: `1px solid ${s.done ? '#10b98144' : '#3a2a12'}`, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.done ? '✅' : `${s.step}`}</div>
            <div style={{ color: s.done ? '#10b981' : '#8a7055', fontSize: 12, fontWeight: s.done ? 700 : 400 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #3a2a12', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ background: 'none', border: 'none', borderBottom: tab === t ? '2px solid #c9a96e' : '2px solid transparent', color: tab === t ? '#c9a96e' : '#8a7055', padding: '8px 16px', cursor: 'pointer', fontWeight: tab === t ? 700 : 400, fontSize: 13, textTransform: 'capitalize', fontFamily: 'inherit' }}>
            {t === 'troubleshoot' ? 'SOPs' : t}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div>
          {content.overview && <p style={{ color: '#d4b896', lineHeight: 1.7, marginBottom: 24 }}>{content.overview}</p>}
          {content.keyPoints?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12 }}>Key Points to Know</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {content.keyPoints.map((k, i) => (
                  <li key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, color: '#d4b896', fontSize: 14 }}>
                    <span style={{ color: '#c9a96e', flexShrink: 0 }}>→</span> {k}
                  </li>
                ))}
              </ul>
            </>
          )}
          {content.systems?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Systems Used</h4>
              {content.systems.map((s, i) => (
                <div key={i} style={{ background: '#2a1f0f', borderRadius: 8, padding: '12px 16px', marginBottom: 10 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 700, marginBottom: 4 }}>{s.name}</div>
                  <div style={{ color: '#8a7055', fontSize: 13 }}>{s.use}</div>
                </div>
              ))}
            </>
          )}
          {content.stanceGuide && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Stance & Technique Guide</h4>
              {Object.entries(content.stanceGuide).map(([k, v]) => (
                <div key={k} style={{ background: '#2a1f0f', borderRadius: 8, padding: '12px 16px', marginBottom: 10 }}>
                  <div style={{ color: '#c9a96e', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 4 }}>{k}</div>
                  <div style={{ color: '#d4b896', fontSize: 13 }}>{v}</div>
                </div>
              ))}
            </>
          )}
          {content.checklist?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Checklist</h4>
              {content.checklist.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, color: '#d4b896', fontSize: 14 }}>
                  <span style={{ color: '#c9a96e' }}>☐</span> {item}
                </div>
              ))}
            </>
          )}
          {content.closing?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Closing Steps</h4>
              {content.closing.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, color: '#d4b896', fontSize: 14 }}>
                  <span style={{ color: '#c9a96e' }}>☐</span> {item}
                </div>
              ))}
            </>
          )}
          {content.handsOn && (
            <div style={{ background: '#6366f118', border: '1px solid #6366f144', borderRadius: 10, padding: 16, marginTop: 24 }}>
              <div style={{ color: '#a5b4fc', fontWeight: 700, marginBottom: 6 }}>✍️ Hands-On Sign-Off Requirement</div>
              <div style={{ color: '#d4b896', fontSize: 13 }}>{content.handsOn}</div>
            </div>
          )}
        </div>
      )}

      {/* Scripts */}
      {tab === 'scripts' && (
        <div>
          <p style={{ color: '#8a7055', fontSize: 14, marginBottom: 20 }}>Learn these scripts closely enough that the message is consistent, but still sound human.</p>
          {(content.scripts || []).map((s, i) => (
            <div key={i} style={{ background: '#2a1f0f', borderRadius: 10, padding: 20, marginBottom: 16, borderLeft: '3px solid #c9a96e' }}>
              <div style={{ color: '#c9a96e', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>{s.label}</div>
              <div style={{ color: '#f5e6c8', fontSize: 15, lineHeight: 1.6, fontStyle: 'italic' }}>"{s.text}"</div>
            </div>
          ))}
          {(!content.scripts || content.scripts.length === 0) && <p style={{ color: '#5a4030' }}>No scripts for this module yet.</p>}
        </div>
      )}

      {/* Videos */}
      {tab === 'videos' && (
        <div>
          {module.videos?.map(v => {
            const done = watched.includes(v.id);
            return (
              <div key={v.id} style={{ background: '#1a1208', border: `1px solid ${done ? '#10b98144' : '#3a2a12'}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#0a0702' }}>
                  <iframe src={v.url} title={v.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allowFullScreen />
                </div>
                <div style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ color: '#f5e6c8', fontWeight: 600 }}>{v.title}</div>
                    {v.duration && <div style={{ color: '#8a7055', fontSize: 12 }}>{v.duration}</div>}
                  </div>
                  {done ? <span style={{ color: '#10b981', fontWeight: 700 }}>✓ Watched</span>
                    : <Btn small onClick={() => markWatched(v)}>Mark as Watched</Btn>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quiz */}
      {tab === 'quiz' && (
        <div>
          {quizResult ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              {quizResult.error === 'cooldown' && (<>
                <div style={{ fontSize: 56, marginBottom: 16 }}>⏳</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 24, marginBottom: 8 }}>Cooldown Period Active</h3>
                <p style={{ color: '#f59e0b', fontSize: 16, marginBottom: 8 }}>Wait <strong>{quizResult.hours_left} hour{quizResult.hours_left !== 1 ? 's' : ''}</strong> before retaking.</p>
                <p style={{ color: '#8a7055', fontSize: 13, marginBottom: 20 }}>Use this time to review the material carefully before your next attempt.</p>
                <div style={{ background: '#f59e0b18', border: '1px solid #f59e0b44', borderRadius: 10, padding: 14, marginBottom: 20 }}>
                  <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 4 }}>Next attempt available:</div>
                  <div style={{ color: '#f5e6c8', fontSize: 13 }}>{new Date(quizResult.next_allowed).toLocaleString()}</div>
                </div>
                <Btn variant="secondary" onClick={() => { setQuizResult(null); setQuizAnswers({}); setTab('overview'); }}>Back to Module</Btn>
              </>)}
              {quizResult.error === 'max_attempts' && (<>
                <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 24, marginBottom: 8 }}>Maximum Attempts Reached</h3>
                <p style={{ color: '#e63c3c', fontSize: 15, marginBottom: 8 }}>You have used all {quizResult.max_attempts} attempts for this quiz.</p>
                <div style={{ background: '#e63c3c18', border: '1px solid #e63c3c44', borderRadius: 10, padding: 14, marginBottom: 20, textAlign: 'left' }}>
                  <div style={{ color: '#e63c3c', fontWeight: 700, marginBottom: 6 }}>What to do next:</div>
                  <div style={{ color: '#d4b896', fontSize: 13 }}>1. Review the module content thoroughly<br/>2. Practice the hands-on skills<br/>3. Ask your manager to unlock the quiz when ready</div>
                </div>
                <Btn variant="secondary" onClick={() => { setQuizResult(null); setQuizAnswers({}); setTab('overview'); }}>Back to Module</Btn>
              </>)}
              {!quizResult.error && (<>
                <div style={{ fontSize: 56, marginBottom: 16 }}>{quizResult.passed ? '🏆' : '📚'}</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 28, marginBottom: 8 }}>{quizResult.passed ? 'Quiz Passed!' : 'Not Quite'}</h3>
                <div style={{ fontSize: 48, fontFamily: "'Playfair Display', serif", color: quizResult.passed ? '#10b981' : '#e63c3c', fontWeight: 700, marginBottom: 8 }}>{quizResult.score}%</div>
                <p style={{ color: '#8a7055', marginBottom: 4 }}>{quizResult.correct} / {quizResult.total} correct · Pass mark: {quizResult.pass_mark}%</p>
                <p style={{ color: '#5a4030', fontSize: 12, marginBottom: 16 }}>Attempt {quizResult.attempts}{quizResult.max_attempts > 0 ? ` of ${quizResult.max_attempts} maximum` : ''}</p>
                {quizResult.passed && <p style={{ color: '#10b981', fontSize: 14, marginBottom: 20 }}>✓ Now awaiting manager hands-on sign-off for full certification.</p>}
                {!quizResult.passed && quizResult.cooldown_hours > 0 && (
                  <div style={{ background: '#f59e0b18', border: '1px solid #f59e0b44', borderRadius: 10, padding: 14, marginBottom: 20 }}>
                    <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>⏳ {quizResult.cooldown_hours}-hour cooldown before next attempt</div>
                    <div style={{ color: '#8a7055', fontSize: 12, marginTop: 4 }}>Review the material carefully before retaking.</div>
                  </div>
                )}
                {!quizResult.passed && !quizResult.cooldown_hours && <p style={{ color: '#e63c3c', fontSize: 14, marginBottom: 20 }}>Review the material and try again.</p>}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  {!quizResult.passed && !quizResult.cooldown_hours && <Btn onClick={() => { setQuizResult(null); setQuizAnswers({}); }}>Try Again</Btn>}
                  {quizResult.passed && <Btn variant="success" onClick={() => setTab('overview')}>Back to Module</Btn>}
                  <Btn variant="secondary" onClick={() => { setQuizResult(null); setQuizAnswers({}); setTab('overview'); }}>Back to Module</Btn>
                </div>
              </>)}
            </div>
          ) : (
            <div>
              <div style={{ background: '#c9a96e18', border: '1px solid #c9a96e44', borderRadius: 10, padding: 16, marginBottom: 24 }}>
                <div style={{ color: '#c9a96e', fontWeight: 700, marginBottom: 4 }}>Quiz Requirements</div>
                <div style={{ color: '#8a7055', fontSize: 13 }}>{module.questions.length} questions · {module.pass_mark}% minimum to pass{module.pass_mark === 90 ? ' (Axe Safety — high bar required)' : ''}</div>
                {progress.quiz_score !== null && progress.quiz_score !== undefined && (
                  <div style={{ color: '#8a7055', fontSize: 13, marginTop: 4 }}>Last attempt: {progress.quiz_score}% · Attempts: {progress.quiz_attempts || 0}</div>
                )}
              </div>
              {module.questions.map((q, qi) => (
                <div key={q.id} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 10, padding: 20, marginBottom: 16 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 600, marginBottom: 16 }}>{qi + 1}. {q.question}</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {q.options.map((opt, oi) => (
                      <div key={oi} onClick={() => setQuizAnswers(p => ({ ...p, [qi]: oi }))} style={{ padding: '12px 16px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${quizAnswers[qi] === oi ? '#c9a96e' : '#3a2a12'}`, background: quizAnswers[qi] === oi ? '#c9a96e18' : '#2a1f0f', color: quizAnswers[qi] === oi ? '#f5e6c8' : '#a08060', transition: 'all 0.12s' }}>
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Btn onClick={submitQuiz} disabled={quizLoading || Object.keys(quizAnswers).length < module.questions.length}>
                {quizLoading ? 'Submitting...' : `Submit Quiz (${Object.keys(quizAnswers).length}/${module.questions.length} answered)`}
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* SOPs / Troubleshooting */}
      {tab === 'troubleshoot' && (
        <div>
          {content.troubleshooting?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 16 }}>Troubleshooting Guide</h4>
              {content.troubleshooting.map((t, i) => (
                <div key={i} style={{ background: '#2a1f0f', borderRadius: 10, padding: 20, marginBottom: 16 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 700, marginBottom: 12 }}>⚠️ {t.issue}</div>
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    {t.steps.map((s, j) => (
                      <li key={j} style={{ color: '#d4b896', fontSize: 13, marginBottom: 8, lineHeight: 1.5 }}>{s}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </>
          )}
          {content.startup?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Startup Procedure</h4>
              {content.startup.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, color: '#d4b896', fontSize: 14 }}>
                  <span style={{ color: '#c9a96e', flexShrink: 0 }}>{i + 1}.</span> {s}
                </div>
              ))}
            </>
          )}
          {content.shutdown?.length > 0 && (
            <>
              <h4 style={{ color: '#c9a96e', marginBottom: 12, marginTop: 24 }}>Shutdown Procedure</h4>
              {content.shutdown.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, color: '#d4b896', fontSize: 14 }}>
                  <span style={{ color: '#c9a96e', flexShrink: 0 }}>{i + 1}.</span> {s}
                </div>
              ))}
            </>
          )}
          {!content.troubleshooting?.length && !content.startup?.length && <p style={{ color: '#5a4030' }}>{['owner','admin'].includes(user.role) ? 'No SOPs added yet. Add them from the Content Editor.' : 'No SOPs for this module yet.'}</p>}
        </div>
      )}
    </div>
  );
}

// ── Staff Progress (Manager) ──────────────────────────────────────────────────
function StaffProgress({ users, modules, allProgress, onRefresh }) {
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [signoffModal, setSignoffModal] = useState(null);
  const [signoffForm, setSignoffForm] = useState({ status: 'passed', notes: '' });

  const staff = users.filter(u => u.role === 'staff' && u.active);

  async function submitSignoff() {
    try {
      await post('/progress/signoff', { user_id: signoffModal.user_id, module_id: signoffModal.module_id, ...signoffForm });
      setSignoffModal(null);
      onRefresh();
    } catch (e) { alert(e.message); }
  }

  function getUserProgress(userId) {
    return allProgress.filter(p => p.user_id === userId);
  }

  function getCertCount(userId) {
    return getUserProgress(userId).filter(p => p.certified).length;
  }

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12}}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8' }}>Staff Progress</h2>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {['owner','admin'].includes(user.role) && <>
          <a href={`/api/export/certifications?token=${localStorage.getItem('tnt_token')}`} target="_blank" style={{background:'#6366f122',color:'#6366f1',border:'1px solid #6366f144',borderRadius:8,padding:'8px 16px',textDecoration:'none',fontSize:12,fontWeight:700}}>📊 Export Certifications CSV</a>
          <a href={`/api/export/incidents?token=${localStorage.getItem('tnt_token')}`} target="_blank" style={{background:'#f59e0b22',color:'#f59e0b',border:'1px solid #f59e0b44',borderRadius:8,padding:'8px 16px',textDecoration:'none',fontSize:12,fontWeight:700}}>📋 Export Incident Log CSV</a>
          <a href={`/api/export/checklists?token=${localStorage.getItem('tnt_token')}`} target="_blank" style={{background:'#10b98122',color:'#10b981',border:'1px solid #10b98144',borderRadius:8,padding:'8px 16px',textDecoration:'none',fontSize:12,fontWeight:700}}>☑️ Export Checklists CSV</a>
          </>}
        </div>
      </div>

      {signoffModal && (
        <Modal onClose={() => setSignoffModal(null)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Hands-On Sign-Off</h3>
          <p style={{ color: '#8a7055', marginBottom: 20 }}>{signoffModal.user_name} — {signoffModal.module_title}</p>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Result</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[{ v: 'passed', label: '✓ Passed', color: '#10b981' }, { v: 'needs_retraining', label: '✗ Needs Retraining', color: '#e63c3c' }].map(o => (
                <div key={o.v} onClick={() => setSignoffForm(p => ({ ...p, status: o.v }))} style={{ padding: 12, borderRadius: 8, cursor: 'pointer', border: `1px solid ${signoffForm.status === o.v ? o.color : '#3a2a12'}`, background: signoffForm.status === o.v ? o.color + '18' : '#2a1f0f', color: signoffForm.status === o.v ? o.color : '#8a7055', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{o.label}</div>
              ))}
            </div>
          </div>
          <Input label="Notes (optional)" value={signoffForm.notes} onChange={e => setSignoffForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Needs more practice explaining waivers" rows={3} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={submitSignoff}>Save Sign-Off</Btn>
            <Btn variant="secondary" onClick={() => setSignoffModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {staff.map(u => {
          const prog = getUserProgress(u.id);
          const certs = getCertCount(u.id);
          const pct = modules.length ? Math.round((certs / modules.length) * 100) : 0;
          const isOpen = selected === u.id;

          return (
            <div key={u.id} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => setSelected(isOpen ? null : u.id)}>
                <Avatar name={u.name} size={42} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 700 }}>{u.name}</div>
                  <div style={{ color: '#8a7055', fontSize: 12 }}>{u.position || 'Staff'}</div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 80 }}>
                  <div style={{ color: '#c9a96e', fontWeight: 700, fontSize: 18 }}>{pct}%</div>
                  <div style={{ color: '#5a4030', fontSize: 11 }}>{certs}/{modules.length} certified</div>
                </div>
                <span style={{ color: '#5a4030', fontSize: 18 }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid #3a2a12', padding: '16px 22px' }}>
                  <ProgressBar pct={pct} height={8} />
                  <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
                    {modules.filter(m => m.status === 'published').map(m => {
                      const p = prog.find(pr => pr.module_id === m.id) || {};
                      const canSignOff = p.quiz_passed && !p.certified && (user.role === 'owner' || user.role === 'admin');
                      return (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#2a1f0f', borderRadius: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#f5e6c8', fontSize: 13, fontWeight: 600 }}>{m.title}</div>
                            <div style={{ color: '#5a4030', fontSize: 11, marginTop: 2 }}>
                              Quiz: {p.quiz_score !== undefined && p.quiz_score !== null ? `${p.quiz_score}%` : '—'} ·
                              Hands-on: {p.hands_on_status || 'pending'}
                              {p.hands_on_signed_by ? ` (by ${p.hands_on_signed_by})` : ''}
                            </div>
                          </div>
                          {p.certified
                            ? <Badge label="Certified" color="#10b981" />
                            : p.quiz_passed
                              ? <Badge label="Quiz Passed" color="#f59e0b" />
                              : <Badge label="In Progress" color="#6366f1" />}
                          {canSignOff && (
                            <Btn small variant="ghost" onClick={() => { setSignoffModal({ user_id: u.id, module_id: m.id, user_name: u.name, module_title: m.title }); setSignoffForm({ status: 'passed', notes: '' }); }}>
                              Sign Off
                            </Btn>
                          )}
                          {!p.quiz_passed && p.quiz_attempts > 0 && ['owner','admin'].includes(user.role) && (
                            <Btn small variant="secondary" onClick={async () => { await post('/progress/quiz/unlock', { user_id: u.id, module_id: m.id }); loadData(); }}>
                              Unlock Quiz
                            </Btn>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {staff.length === 0 && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>No staff accounts yet. Add staff in the Users panel.</p>}
      </div>
    </div>
  );
}

// ── User Management ───────────────────────────────────────────────────────────
function UserManagement({ users, onRefresh }) {
  const { user } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '', role: 'staff', position: '' });
  const [resetModal, setResetModal] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [error, setError] = useState('');

  const canManage = ['owner', 'admin'].includes(user.role);

  async function addUser() {
    setError('');
    try {
      await post('/users', form);
      setShowAdd(false);
      setForm({ name: '', username: '', password: '', role: 'staff', position: '' });
      onRefresh();
    } catch (e) { setError(e.message); }
  }

  async function toggleActive(u) {
    try { await put(`/users/${u.id}`, { ...u, active: u.active ? 0 : 1 }); onRefresh(); }
    catch (e) { alert(e.message); }
  }

  async function resetPassword() {
    try { await post(`/users/${resetModal.id}/reset-password`, { new_password: newPw }); setResetModal(null); setNewPw(''); }
    catch (e) { alert(e.message); }
  }

  async function hardDelete(userId) {
    try { await del(`/users/${userId}`); onRefresh(); }
    catch (e) { alert(e.message); }
  }

  const [positions, setPositions] = useState([]);
  const [showPositionManager, setShowPositionManager] = useState(false);
  const [newPosition, setNewPosition] = useState('');
  const [posError, setPosError] = useState('');

  const loadPositions = useCallback(async () => {
    try { setPositions(await get('/positions')); } catch(e) {}
  }, []);

  useEffect(() => { loadPositions(); }, [loadPositions]);

  async function addPosition() {
    setPosError('');
    if (!newPosition.trim()) return;
    try { await post('/positions', { title: newPosition.trim() }); setNewPosition(''); loadPositions(); }
    catch(e) { setPosError(e.message); }
  }

  async function removePosition(id) {
    try { await del('/positions/' + id); loadPositions(); } catch(e) { alert(e.message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8' }}>User Management</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          {user.role === 'owner' && <Btn variant="ghost" small onClick={() => setShowPositionManager(true)}>⚙️ Manage Positions</Btn>}
          {canManage && <Btn onClick={() => setShowAdd(true)}>+ Add Staff</Btn>}
        </div>
      </div>

      {showPositionManager && user.role === 'owner' && (
        <Modal onClose={() => setShowPositionManager(false)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Manage Positions</h3>
          <p style={{ color: '#8a7055', fontSize: 13, marginBottom: 20 }}>Add or remove positions. These appear in the dropdown when creating staff accounts.</p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <input value={newPosition} onChange={e => setNewPosition(e.target.value)} onKeyDown={e => e.key === 'Enter' && addPosition()} placeholder="e.g. Guest Experience Host"
              style={{ flex: 1, background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }} />
            <Btn onClick={addPosition} disabled={!newPosition.trim()}>Add</Btn>
          </div>
          {posError && <p style={{ color: '#e63c3c', fontSize: 12, marginBottom: 8 }}>{posError}</p>}
          <div style={{ display: 'grid', gap: 8, maxHeight: 340, overflowY: 'auto', marginTop: 12 }}>
            {positions.map(p => (
              <div key={p.id} style={{ background: '#2a1f0f', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color || '#c9a96e', flexShrink: 0 }} />
                  <span style={{ color: '#f5e6c8', fontSize: 14 }}>{p.title}</span>
                </div>
                <Btn small variant="danger" onClick={() => removePosition(p.id)}>Remove</Btn>
              </div>
            ))}
          </div>
          <p style={{ color: '#5a4030', fontSize: 11, marginTop: 12 }}>Removing a position does not affect existing staff — it just removes it from the dropdown for new accounts.</p>
          <div style={{ marginTop: 16 }}><Btn variant="secondary" onClick={() => setShowPositionManager(false)}>Done</Btn></div>
        </Modal>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 20 }}>Add New Staff Member</h3>
          <Input label="Full Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Alex Nguyen" />
          <Input label="Username" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="e.g. alex (lowercase, no spaces)" />
          <Input label="Temporary Password" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="They'll be prompted to change it" />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Position</label>
            <select value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
              <option value="">Select position...</option>
              {positions.map(p => <option key={p.id} value={p.title}>{p.title}</option>)}
            </select>
          </div>
          {user.role === 'owner' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
                <option value="staff">Staff</option>
                <option value="lead">Lead (Bar Lead, Kitchen Lead, etc.)</option>
                <option value="admin">Manager (GM)</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          )}
          {error && <p style={{ color: '#e63c3c', fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={addUser} disabled={!form.name || !form.username || !form.password}>Create Account</Btn>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {resetModal && (
        <Modal onClose={() => setResetModal(null)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Reset Password</h3>
          <p style={{ color: '#8a7055', marginBottom: 20 }}>Reset password for {resetModal.name}</p>
          <Input label="New Password" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Minimum 4 characters" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={resetPassword} disabled={newPw.length < 4}>Reset Password</Btn>
            <Btn variant="secondary" onClick={() => setResetModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {users.map(u => (
          <div key={u.id} style={{ background: '#1a1208', border: `1px solid ${u.active ? '#3a2a12' : '#2a1510'}`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, opacity: u.active ? 1 : 0.6 }}>
            <Avatar name={u.name} size={38} />
            <div style={{ flex: 1 }}>
              <div style={{ color: u.active ? '#f5e6c8' : '#8a7055', fontWeight: 600 }}>{u.name}</div>
              <div style={{ color: '#5a4030', fontSize: 12 }}>@{u.username} · {u.position || 'No position'}</div>
              <div style={{ color: '#5a4030', fontSize: 11, marginTop: 2 }}>
                {u.last_login ? `Last login: ${new Date(u.last_login + 'Z').toLocaleString()}` : 'Never logged in'}
                {u.login_count > 0 ? ` · ${u.login_count} login${u.login_count === 1 ? '' : 's'}` : ''}
              </div>
            </div>
            <Badge label={roleLabel[u.role] || u.role} color={roleColor[u.role] || '#8a7055'} />
            {canManage && u.id !== user.id && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn small variant="ghost" onClick={() => { setResetModal(u); setNewPw(''); }}>Reset PW</Btn>
                {(user.role === 'owner' || u.role === 'staff') && (
                  <Btn small variant={u.active ? 'danger' : 'success'} onClick={() => toggleActive(u)}>
                    {u.active ? 'Deactivate' : 'Activate'}
                  </Btn>
                )}
                {!u.active && user.role === 'owner' && (
                  <Btn small variant="danger" onClick={() => {
                    if (window.confirm(`Permanently delete ${u.name}? This cannot be undone and will remove all their training records.`)) {
                      hardDelete(u.id);
                    }
                  }}>🗑 Delete</Btn>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Change Comparison (old vs new) ────────────────────────────────────────────
function ChangeComparison({ request }) {
  const { payload = {}, original } = request;

  // Friendly field labels
  const fieldLabels = {
    title: 'Title', description: 'Description', pass_mark: 'Pass Mark %',
    status: 'Status', hands_on_required: 'Hands-On Sign-Off', category: 'Category',
    url: 'Link / URL', file_type: 'File Type', days: 'Schedule (Days & Items)',
    name: 'Name', price: 'Price', subcategory: 'Subcategory',
    checklist_type: 'Checklist Type', items: 'Checklist Items',
    document_id: null, template_id: null, item_id: null, // internal — don't show
  };

  function fmt(val) {
    if (val === undefined || val === null || val === '') return '(empty)';
    if (val === 1 || val === true) return 'Yes';
    if (val === 0 || val === false) return 'No';
    if (Array.isArray(val)) {
      // Onboarding "days" have nested items; checklist items have a label
      if (val.length && val[0] && val[0].items !== undefined) {
        const itemCount = val.reduce((a, d) => a + (d.items?.length || 0), 0);
        return `${val.length} day(s), ${itemCount} item(s)`;
      }
      if (val.length && val[0] && val[0].label !== undefined) {
        return `${val.length} item(s): ` + val.map(i => i.label).filter(Boolean).slice(0, 8).join('; ') + (val.length > 8 ? '…' : '');
      }
      return `${val.length} item(s)`;
    }
    return String(val);
  }

  // Build the list of fields to show (skip internal ids and unchanged-empty)
  const keys = Object.keys(payload).filter(k => fieldLabels[k] !== null);

  // Delete requests are special — just confirm what's being deleted
  if (request.change_type === 'delete_document') {
    return (
      <div style={{ background: '#e63c3c14', border: '1px solid #e63c3c44', borderRadius: 10, padding: 18, marginBottom: 20 }}>
        <div style={{ color: '#e63c3c', fontWeight: 700, marginBottom: 6 }}>🗑️ Request to DELETE this document:</div>
        <div style={{ color: '#f5e6c8', fontSize: 15 }}>{payload.title || original?.title || 'Document'}</div>
        <div style={{ color: '#8a7055', fontSize: 12, marginTop: 8 }}>If approved, this document will be removed from the Document Vault.</div>
      </div>
    );
  }

  if (request.change_type === 'menu_delete') {
    return (
      <div style={{ background: '#e63c3c14', border: '1px solid #e63c3c44', borderRadius: 10, padding: 18, marginBottom: 20 }}>
        <div style={{ color: '#e63c3c', fontWeight: 700, marginBottom: 6 }}>🗑️ Request to DELETE this menu item:</div>
        <div style={{ color: '#f5e6c8', fontSize: 15 }}>{payload.name || 'Menu item'}</div>
        <div style={{ color: '#8a7055', fontSize: 12, marginTop: 8 }}>If approved, this item will be removed from the Menu Catalog.</div>
      </div>
    );
  }

  if (request.change_type === 'checklist_delete') {
    return (
      <div style={{ background: '#e63c3c14', border: '1px solid #e63c3c44', borderRadius: 10, padding: 18, marginBottom: 20 }}>
        <div style={{ color: '#e63c3c', fontWeight: 700, marginBottom: 6 }}>🗑️ Request to DELETE this checklist:</div>
        <div style={{ color: '#f5e6c8', fontSize: 15 }}>{payload.title || 'Checklist'}</div>
        <div style={{ color: '#8a7055', fontSize: 12, marginTop: 8 }}>If approved, this checklist will be removed.</div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      {!original && (
        <div style={{ background: '#6366f114', border: '1px solid #6366f144', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#a5b4fc', fontSize: 12 }}>
          This is a brand-new item, so there's nothing to compare against — below is what will be created.
        </div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {keys.map(k => {
          const newVal = fmt(payload[k]);
          const oldVal = original ? fmt(original[k]) : null;
          const changed = original ? (JSON.stringify(payload[k]) !== JSON.stringify(original[k])) : true;
          return (
            <div key={k} style={{ background: changed ? '#f59e0b10' : '#1a1208', border: `1px solid ${changed ? '#f59e0b55' : '#2a1f0f'}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ color: '#8a7055', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{fieldLabels[k] || k}</span>
                {changed && <span style={{ background: '#f59e0b22', color: '#f59e0b', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>CHANGED</span>}
                {!changed && <span style={{ color: '#5a4030', fontSize: 10 }}>unchanged</span>}
              </div>
              {original && changed ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ color: '#e63c3c', fontSize: 13, textDecoration: 'line-through', opacity: 0.8 }}>− {oldVal}</div>
                  <div style={{ color: '#10b981', fontSize: 14, fontWeight: 600 }}>+ {newVal}</div>
                </div>
              ) : (
                <div style={{ color: '#f5e6c8', fontSize: 14 }}>{newVal}</div>
              )}
            </div>
          );
        })}
      </div>
      {keys.some(k => k === 'days') && (
        <p style={{ color: '#5a4030', fontSize: 11, marginTop: 10 }}>Note: the schedule shows a summary. Approving applies the full day-by-day changes Max made.</p>
      )}
    </div>
  );
}

// ── Change Requests (Owner) ───────────────────────────────────────────────────
function ChangeRequests({ onRefresh }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [notes, setNotes] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedPayload, setEditedPayload] = useState(null);
  const { user } = useAuth();

  const loadRequests = useCallback(async () => {
    try { setRequests(await get('/changes')); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  function openReview(r) {
    setReviewModal(r);
    setNotes('');
    setEditMode(false);
    setEditedPayload({ ...r.payload });
  }

  async function review(action) {
    try {
      // If owner edited the proposed version, send their edits along on approve
      const body = { action, notes };
      if (action === 'approve' && editMode && editedPayload) body.edited_payload = editedPayload;
      await post(`/changes/${reviewModal.id}/review`, body);
      setReviewModal(null); setNotes(''); setEditMode(false); setEditedPayload(null);
      loadRequests(); onRefresh();
    } catch (e) { alert(e.message); }
  }

  async function cancelRequest(id) {
    if (!window.confirm('Cancel this request? It will be removed and not sent to the owner. You can make a fresh edit afterward if you want.')) return;
    try {
      await del(`/changes/${id}`);
      loadRequests(); onRefresh();
    } catch (e) { alert(e.message); }
  }

  // Manager editing their OWN pending request
  const [myEditModal, setMyEditModal] = useState(null);
  const [myEditPayload, setMyEditPayload] = useState(null);

  function openMyEdit(r) {
    setMyEditModal(r);
    setMyEditPayload({ ...r.payload });
  }

  async function saveMyEdit() {
    try {
      await put(`/changes/${myEditModal.id}`, myEditPayload);
      setMyEditModal(null); setMyEditPayload(null);
      loadRequests(); onRefresh();
    } catch (e) { alert(e.message); }
  }

  const pending = requests.filter(r => r.status === 'pending');
  const reviewed = requests.filter(r => r.status !== 'pending');
  const statusColor = { pending: '#f59e0b', approved: '#10b981', rejected: '#e63c3c' };
  const typeLabel = { module_edit: 'Module Edit', new_module: 'New Module', new_question: 'New Question', edit_question: 'Edit Question', new_video: 'New Video', new_document: 'New Document', delete_document: '🗑️ Delete Document', quickref_edit: 'Quick Ref Edit', edit_onboarding: '📋 Onboarding Edit', menu_add: '🍹 New Menu Item', menu_edit: '🍹 Menu Item Edit', menu_delete: '🗑️ Delete Menu Item', checklist_add: '☑️ New Checklist', checklist_edit: '☑️ Checklist Edit', checklist_delete: '🗑️ Delete Checklist' };

  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 8 }}>Change Requests</h2>
      <p style={{ color: '#8a7055', marginBottom: 24 }}>
        {user.role === 'owner' ? 'Review and approve content changes submitted by your managers.' : 'Track your submitted change requests.'}
      </p>

      {reviewModal && user.role === 'owner' && (
        <Modal onClose={() => setReviewModal(null)} wide>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Review Change Request</h3>
          <p style={{ color: '#8a7055', marginBottom: 4 }}>From: {reviewModal.requester_name} · Module: {reviewModal.module_title || 'New Module'}</p>
          <p style={{ color: '#c9a96e', fontSize: 12, marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{typeLabel[reviewModal.change_type]}</p>

          {!editMode ? (
            <ChangeComparison request={reviewModal} />
          ) : (
            <div style={{ background: '#6366f10f', border: '1px solid #6366f144', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ color: '#a5b4fc', fontSize: 12, fontWeight: 700, marginBottom: 14 }}>✏️ Editing the proposed version — your changes here will be what goes live when you approve.</div>
              {editedPayload && 'title' in editedPayload && (
                <Input label="Title" value={editedPayload.title || ''} onChange={e => setEditedPayload(p => ({ ...p, title: e.target.value }))} />
              )}
              {editedPayload && 'description' in editedPayload && (
                <Input label="Description" value={editedPayload.description || ''} onChange={e => setEditedPayload(p => ({ ...p, description: e.target.value }))} rows={3} />
              )}
              {editedPayload && 'pass_mark' in editedPayload && (
                <Input label="Pass Mark %" type="number" value={editedPayload.pass_mark ?? ''} onChange={e => setEditedPayload(p => ({ ...p, pass_mark: Number(e.target.value) }))} />
              )}
              {editedPayload && 'status' in editedPayload && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
                  <select value={editedPayload.status || 'draft'} onChange={e => setEditedPayload(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
                    <option value="draft">Draft (hidden from staff)</option>
                    <option value="published">Published (visible to staff)</option>
                  </select>
                </div>
              )}
              <p style={{ color: '#5a4030', fontSize: 11 }}>Note: complex fields like the day-by-day schedule keep Max's submitted version; you can edit those directly in the section after approving.</p>
            </div>
          )}

          <Input label="Review Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add a note for the requester..." rows={2} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {!editMode && reviewModal.change_type !== 'delete_document' && (
              <Btn variant="ghost" onClick={() => setEditMode(true)}>✏️ Edit This Version</Btn>
            )}
            {editMode ? (
              <>
                <Btn variant="success" onClick={() => review('approve')}>✓ Approve with My Edits</Btn>
                <Btn variant="secondary" onClick={() => { setEditMode(false); setEditedPayload({ ...reviewModal.payload }); }}>↩ Back to Original</Btn>
              </>
            ) : (
              <>
                <Btn variant="success" onClick={() => review('approve')}>✓ Approve & Apply</Btn>
                <Btn variant="danger" onClick={() => review('reject')}>✗ Reject</Btn>
              </>
            )}
            <Btn variant="secondary" onClick={() => { setReviewModal(null); setEditMode(false); }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {myEditModal && user.role !== 'owner' && (
        <Modal onClose={() => setMyEditModal(null)} wide>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Edit My Request</h3>
          <p style={{ color: '#8a7055', marginBottom: 4 }}>{typeLabel[myEditModal.change_type] || myEditModal.change_type}</p>
          <p style={{ color: '#5a4030', fontSize: 12, marginBottom: 20 }}>Make changes to your pending request before Kevin reviews it. This updates your existing request — it stays pending.</p>
          <div style={{ background: '#6366f10f', border: '1px solid #6366f144', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            {myEditPayload && 'title' in myEditPayload && (
              <Input label="Title" value={myEditPayload.title || ''} onChange={e => setMyEditPayload(p => ({ ...p, title: e.target.value }))} />
            )}
            {myEditPayload && 'description' in myEditPayload && (
              <Input label="Description" value={myEditPayload.description || ''} onChange={e => setMyEditPayload(p => ({ ...p, description: e.target.value }))} rows={3} />
            )}
            {myEditPayload && 'pass_mark' in myEditPayload && (
              <Input label="Pass Mark %" type="number" value={myEditPayload.pass_mark ?? ''} onChange={e => setMyEditPayload(p => ({ ...p, pass_mark: Number(e.target.value) }))} />
            )}
            {myEditPayload && 'status' in myEditPayload && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</label>
                <select value={myEditPayload.status || 'draft'} onChange={e => setMyEditPayload(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, fontFamily: 'inherit' }}>
                  <option value="draft">Draft (hidden from staff)</option>
                  <option value="published">Published (visible to staff)</option>
                </select>
              </div>
            )}
            <p style={{ color: '#5a4030', fontSize: 11 }}>Note: detailed fields like the day-by-day schedule keep your original submission. To change those, cancel and re-submit from the section editor.</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="success" onClick={saveMyEdit}>Save Changes to Request</Btn>
            <Btn variant="secondary" onClick={() => setMyEditModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {pending.length > 0 && (
        <>
          <h4 style={{ color: '#f59e0b', marginBottom: 12 }}>⏳ Pending ({pending.length})</h4>
          <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
            {pending.map(r => (
              <div key={r.id} style={{ background: '#1a1208', border: '1px solid #f59e0b44', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f5e6c8', fontWeight: 600 }}>{typeLabel[r.change_type] || r.change_type}</div>
                  <div style={{ color: '#8a7055', fontSize: 12 }}>By {r.requester_name} · {r.module_title || 'New Module'} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                {user.role === 'owner'
                  ? <Btn small variant="ghost" onClick={() => openReview(r)}>Review</Btn>
                  : (r.requested_by === user.id && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.change_type !== 'delete_document' && <Btn small variant="ghost" onClick={() => openMyEdit(r)}>Edit</Btn>}
                      <Btn small variant="danger" onClick={() => cancelRequest(r.id)}>Cancel</Btn>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </>
      )}

      {reviewed.length > 0 && (
        <>
          <h4 style={{ color: '#8a7055', marginBottom: 12 }}>History</h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {reviewed.map(r => (
              <div key={r.id} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14, opacity: 0.8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f5e6c8', fontSize: 13 }}>{typeLabel[r.change_type]} — {r.module_title || 'New Module'}</div>
                  <div style={{ color: '#5a4030', fontSize: 12 }}>By {r.requester_name} · Reviewed by {r.reviewed_by || '—'}</div>
                </div>
                <Badge label={r.status} color={statusColor[r.status] || '#8a7055'} />
              </div>
            ))}
          </div>
        </>
      )}

      {loading && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>Loading...</p>}
      {!loading && requests.length === 0 && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>No change requests yet.</p>}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
function AppInner() {
  const { user, logout, loading } = useAuth();
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_STORAGE_KEY) || 'dashboard'; } catch { return 'dashboard'; }
  });
  const [opsHubOpen, setOpsHubOpen] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const pickView = (k) => { setView(k); setActiveModule(null); setSidebarOpen(false); };
  const [activeModule, setActiveModule] = useState(null);
  const [allModules, setModules] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [allProgress, setAllProgress] = useState([]);
  const [users, setUsers] = useState([]);
  const [branding, setBranding] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showPwModal, setShowPwModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewRole, setPreviewRole] = useState(null);

  // When the owner is "viewing as" another role, effectiveRole drives what the UI shows.
  // This NEVER changes the real account or its server permissions — it only filters the owner's own view.
  const realRole = user?.role;
  const effectiveRole = (realRole === 'owner' && previewRole) ? previewRole : realRole;
  const isManager = ['owner', 'admin'].includes(effectiveRole);
  const isGM = ['owner', 'admin'].includes(effectiveRole);

  // Persist the active view so a browser refresh stays on the same page.
  useEffect(() => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch {}
  }, [view]);

  // If a persisted manager-only view is restored for a non-manager, fall back to the dashboard.
  useEffect(() => {
    if (!isManager && MANAGER_VIEWS.has(view)) setView('dashboard');
  }, [isManager, view]);

  const loadData = useCallback(async () => {
    const realIsManager = ['owner', 'admin'].includes(user?.role);
    const realIsGM = ['owner', 'admin'].includes(user?.role);
    try {
      const [mods, prog, brand] = await Promise.all([get('/modules'), get('/progress/my'), get('/branding')]);
      setModules(mods);
      setMyProgress(prog);
      setBranding(brand);
      if (realIsManager) {
        const ap = await get('/progress/all');
        setAllProgress(ap);
      }
      if (realIsGM) {
        const us = await get('/users');
        setUsers(us);
      }
    } catch (e) { console.error(e); }
    finally { setDataLoading(false); }
  }, [user]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  if (loading) return <div style={{ minHeight: '100vh', background: '#0a0702', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c9a96e', fontFamily: 'serif', fontSize: 22 }}>🪓 Loading...</div>;
  if (!user) return <Login />;
  if (user.first_login) return <ChangePasswordModal required onClose={() => {}} />;
  if (showWelcome) return <FirstDayWelcome user={user} branding={branding} onDismiss={() => setShowWelcome(false)} />;

  const teamItems = [
    { key: 'modules', label: 'Training', icon: '📋' },
    { key: 'onboarding', label: 'Onboarding', icon: '🗓' },
    { key: 'acknowledgments', label: 'Sign-Offs', icon: '✍️' },
    { key: 'quickref', label: 'Quick Reference', icon: '⚡' },
    { key: 'faq', label: 'FAQ', icon: '❓' },
    { key: 'leaderboard', label: 'Leaderboard', icon: '🏆' },
    { key: 'incidents', label: 'Shift Notes', icon: '📝' },
    { key: 'equipment', label: 'Equipment', icon: '🔧' },
    { key: 'checklists', label: 'Checklists', icon: '☑️' },
    { key: 'menu', label: 'Menu Catalog', icon: '🍹' },
    { key: 'announcements', label: 'Announcements', icon: '📢' },
  ];
  const opsItems = [
    { key: 'careers', label: 'Careers', icon: '📣', ready: true },
    { key: 'applicationform', label: 'Application Form', icon: '📝', ready: true },
    { key: 'hiring', label: 'Candidates', icon: '👤', ready: true },
    { key: 'offers', label: 'Offer & Emails', icon: '✉️', ready: true },
    { key: 'schedule', label: 'Schedule', icon: '📅', ready: true },
    { key: 'scan', label: 'Scan Inbox', icon: '📥', ready: true },
    { key: 'assistant', label: 'Assistant', icon: '🤖', ready: true },
    { key: 'sops', label: 'Manager SOPs', icon: '📖', ready: true },
  ];
  const manageItems = [
    ...(isManager ? [
      { key: 'staff', label: 'Staff Progress', icon: '👥' },
      { key: 'content', label: 'Content Editor', icon: '✏️' },
      { key: 'quickrefedit', label: 'Quick Ref Editor', icon: '⚡' },
      { key: 'changes', label: 'Change Requests', icon: '🔄' },
    ] : []),
    ...(isGM ? [
      { key: 'profiles', label: 'Staff Profiles', icon: '👤' },
      { key: 'compliance', label: 'Compliance', icon: '📊' },
      { key: 'documents', label: 'Documents', icon: '📁' },
      { key: 'users', label: 'Users', icon: '⚙️' },
      { key: 'settings', label: 'Settings', icon: '🎨' },
    ] : []),
  ];

  // When previewing as a lower role, hide modules that role wouldn't see (matches server gating).
  const roleRank = { staff: 1, lead: 2, admin: 3, owner: 4 };
  const previewing = realRole === 'owner' && previewRole;
  const modules = previewing
    ? allModules.filter(m => (m.status === 'published') && (roleRank[effectiveRole] || 1) >= (roleRank[m.min_role] || 1))
    : allModules;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0702', fontFamily: "'DM Sans', sans-serif", color: '#f5e6c8', display: 'flex' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99, display: 'none' }} className="mobile-overlay" />
      )}
      {/* Mobile hamburger button */}
      <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ position: 'fixed', top: 14, left: 14, zIndex: 200, background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 8, color: '#c9a96e', fontSize: 20, width: 40, height: 40, cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }} className="mobile-hamburger">
        {sidebarOpen ? '✕' : '☰'}
      </button>
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} style={{ width: 220, background: '#0f0a05', borderRight: '1px solid #2a1f0f', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', flexShrink: 0 }}>
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid #2a1f0f' }}>
          {branding?.logo_url
            ? <img src={branding.logo_url} alt="Logo" style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain', marginBottom: 8 }} onError={e=>{e.target.style.display='none'}} />
            : <div style={{ fontSize: 28, marginBottom: 6 }}>🪓</div>}
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: '#f5e6c8', lineHeight: 1.2 }}>Tee & Timber</div>
          <div style={{ fontSize: 10, color: '#c9a96e', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>Training Hub</div>
        </div>

        {realRole === 'owner' && (
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #2a1f0f' }}>
            <div style={{ color: '#5a4030', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Preview / View As</div>
            <select value={previewRole || 'owner'} onChange={e => { setPreviewRole(e.target.value === 'owner' ? null : e.target.value); setView('dashboard'); setActiveModule(null); setSidebarOpen(false); }} style={{ width: '100%', background: '#2a1f0f', border: `1px solid ${previewRole ? '#f59e0b' : '#3a2a12'}`, borderRadius: 6, color: previewRole ? '#f59e0b' : '#8a7055', fontSize: 12, padding: '8px', fontFamily: 'inherit', cursor: 'pointer' }}>
              <option value="owner">My View (Owner)</option>
              <option value="admin">View as Manager/GM</option>
              <option value="lead">View as Lead</option>
              <option value="staff">View as Staff</option>
            </select>
          </div>
        )}

        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto', overflowX: 'hidden' }}>
          <NavRow item={{ key: 'dashboard', label: 'Dashboard', icon: '⊞' }} view={view} onPick={pickView} />
          {isManager && <NavGroup icon="🧰" label="Operations Hub" items={opsItems} view={view} onPick={pickView} open={opsHubOpen} onToggle={() => setOpsHubOpen(o => !o)} />}
          {isManager && <div style={{ fontSize: 9, color: '#5a4030', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 12px 4px', fontWeight: 700 }}>Team</div>}
          {teamItems.map(n => <NavRow key={n.key} item={n} view={view} onPick={pickView} />)}
          {isManager && <NavGroup icon="⚙️" label="Manage" items={manageItems} view={view} onPick={pickView} open={manageOpen} onToggle={() => setManageOpen(o => !o)} />}
        </nav>

        <div style={{ padding: '16px 14px', borderTop: '1px solid #2a1f0f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Avatar name={user.name} size={32} />
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ color: '#f5e6c8', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
              <div style={{ color: '#5a4030', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{roleLabel[user.role]}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setShowPwModal(true)} style={{ flex: 1, background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 6, color: '#8a7055', fontSize: 11, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>Password</button>
            <button onClick={logout} style={{ flex: 1, background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 6, color: '#8a7055', fontSize: 11, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit' }}>Sign Out</button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '40px 48px', maxWidth: ['hiring', 'schedule'].includes(view) ? 'none' : 960, overflowY: 'auto' }}>
        {previewing && (
          <div style={{ background: '#f59e0b18', border: '1px solid #f59e0b66', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ color: '#f59e0b', fontSize: 13, fontWeight: 700 }}>
              👁 Previewing as {previewRole === 'admin' ? 'Manager/GM' : previewRole === 'lead' ? 'Lead' : 'Staff'} — this is what they see. Your real access is unchanged.
            </div>
            <button onClick={() => { setPreviewRole(null); setView('dashboard'); }} style={{ background: '#f59e0b', border: 'none', borderRadius: 6, color: '#1a1208', fontSize: 12, fontWeight: 700, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Exit Preview</button>
          </div>
        )}
        {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}

        {dataLoading ? (
          <div style={{ textAlign: 'center', paddingTop: 100, color: '#5a4030' }}>Loading training data...</div>
        ) : (
          <>
            {view === 'dashboard' && !activeModule && (
              <>
                <ExpiryBanner modules={modules} myProgress={myProgress} />
                <Dashboard modules={modules} myProgress={myProgress} allProgress={allProgress} users={users} setView={setView} />
              </>
            )}

            {view === 'announcements' && <AnnouncementsView isManager={isManager} />}

            {view === 'modules' && !activeModule && (
              <div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 8 }}>Training Modules</h2>
                <p style={{ color: '#8a7055', marginBottom: 24 }}>Complete all 3 steps — watch, quiz, and hands-on sign-off — to get certified in each module.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
                  {modules.filter(m => m.status === 'published' || isManager).map(m => {
                    const p = myProgress.find(pr => pr.module_id === m.id) || {};
                    const cc = catColor[m.category] || '#c9a96e';
                    return (
                      <div key={m.id} onClick={() => setActiveModule(m)} style={{ background: '#1a1208', border: `1px solid ${p.certified ? '#10b98144' : '#3a2a12'}`, borderRadius: 12, padding: 22, cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <Badge label={m.category} color={cc} />
                          {m.status === 'draft' && <Badge label="Draft" color="#8a7055" />}
                          {p.certified && <span style={{ fontSize: 18 }}>🏆</span>}
                        </div>
                        <div style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 17, marginBottom: 6, lineHeight: 1.3 }}>{m.title}</div>
                        <div style={{ color: '#8a7055', fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>{m.description?.slice(0, 80)}...</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {[{ label: 'Watch', done: p.videos_watched?.length > 0 || !m.videos?.length }, { label: 'Quiz', done: !!p.quiz_passed }, { label: 'Sign-Off', done: !!p.certified }].map(s => (
                            <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '5px 0', borderRadius: 6, background: s.done ? '#10b98118' : '#2a1f0f', border: `1px solid ${s.done ? '#10b98144' : '#3a2a12'}`, color: s.done ? '#10b981' : '#5a4030', fontSize: 10, fontWeight: 700 }}>
                              {s.done ? '✓' : '○'} {s.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeModule && (
              <div>
                <button onClick={() => setActiveModule(null)} style={{ background: 'none', border: 'none', color: '#8a7055', cursor: 'pointer', fontSize: 13, marginBottom: 20, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                  ← Back to Modules
                </button>
                <ModuleView module={activeModule} myProgress={myProgress} onProgress={() => { loadData(); }} />
              </div>
            )}

            {view === 'staff' && isManager && <StaffProgress users={users} modules={modules.filter(m => m.status === 'published')} allProgress={allProgress} onRefresh={loadData} isGM={isGM} />}
            {view === 'onboarding' && <OnboardingView isManager={isManager} userId={user.id} isOwner={user?.role === 'owner'} isGM={isGM} />}
            {view === 'acknowledgments' && <AcknowledgmentsView isManager={isManager} isGM={isGM} />}
            {view === 'incidents' && <IncidentsView isManager={isManager} isOwner={user?.role === 'owner'} isGM={isGM} />}
            {view === 'quickref' && <QuickReferenceView />}
            {view === 'faq' && <FAQView isManager={isManager} />}
            {view === 'sops' && isManager && <ManagerSOPsView />}
            {view === 'applicationform' && isManager && <ApplicationFormAdmin />}
            {view === 'assistant' && isManager && <ManagerAssistant />}
            {view === 'offers' && isManager && <OffersView />}
            {view === 'leaderboard' && <LeaderboardView currentUserId={user.id} />}
            {view === 'equipment' && <EquipmentTracker isManager={isManager} />}
            {view === 'checklists' && <ChecklistsView isManager={isManager} />}
            {view === 'menu' && <MenuCatalog isManager={isManager} isOwner={user?.role === 'owner'} />}
            {view === 'temps' && <TempLogger isManager={isManager} />}
            {view === 'compliance' && isGM && <ComplianceTracker users={users} isOwner={user.role === 'owner'} />}
            {view === 'documents' && isGM && <DocumentVault isManager={isGM} isOwner={user?.role === 'owner'} />}
            {view === 'profiles' && isGM && <StaffProfilesView users={users} onRefresh={loadData} />}
            {view === 'content' && isManager && <ContentEditor modules={allModules} onRefresh={loadData} userRole={user.role} />}
            {view === 'quickrefedit' && isManager && <QuickRefEditor userRole={user.role} />}
            {view === 'users' && isGM && <UserManagement users={users} onRefresh={loadData} />}
            {view === 'changes' && isManager && <ChangeRequests onRefresh={loadData} />}
            {view === 'careers' && isManager && <CareersAdminView />}
            {view === 'hiring' && isManager && <CandidatesView />}
            {view === 'schedule' && isManager && <ScheduleView />}
            {view === 'scan' && isManager && <ScanInboxView />}
            {view === 'settings' && isGM && <BrandingSettings />}
          </>
        )}
      </main>
    </div>
  );
}

export default function App() {
  // Public, login-free candidate offer-signing page: /sign/<token>
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/sign/')) {
    const token = decodeURIComponent(path.slice('/sign/'.length).split(/[/?#]/)[0]);
    return <OfferSign token={token} />;
  }
  return <AuthProvider><AppInner /></AuthProvider>;
}
 1
//x
